import { Client } from '@app/core/types/client';
import { User } from '@mi-front/core';
import { LocaleRelativeTime } from '@mi-front/ui';
import DeleteIcon from '@mui/icons-material/Delete';
import LoginIcon from '@mui/icons-material/Login';
import { Box, IconButton, ListItemSecondaryAction, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { impersonationMessages } from '../impersonation-messages';
import { ImpersonationUserListItem } from './ImpersonationUserListItem';

type RecentUser = (User & { client: Client }) & {
  lastUsed: number;
};

interface RecentImpersonationUserRowProps {
  user: RecentUser;
  onSelect: (user: RecentUser) => void;
  onDelete: (userId: string, event: React.MouseEvent) => void;
}

/**
 * Clickable recent-user row: impersonate on click, shows last-used time and delete.
 */
export function RecentImpersonationUserRow({
  user,
  onSelect,
  onDelete,
}: RecentImpersonationUserRowProps) {
  const { $t } = useIntl();

  return (
    <ImpersonationUserListItem
      user={user}
      button
      onClick={() => onSelect(user)}
      sx={{
        // Room for last-used time + action icons in the secondary action slot.
        pr: 18,
        '&:hover .impersonation-login-icon': {
          color: 'primary.main',
          opacity: 1,
        },
      }}
    >
      <ListItemSecondaryAction>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            <LocaleRelativeTime value={user.lastUsed} />
          </Typography>
          <Tooltip title={$t(impersonationMessages.impersonateAriaLabel)}>
            <LoginIcon
              className="impersonation-login-icon"
              fontSize="small"
              color="action"
              sx={{ opacity: 0.7 }}
            />
          </Tooltip>
          <IconButton
            edge="end"
            aria-label={$t(impersonationMessages.deleteAriaLabel)}
            onClick={(e) => onDelete(user.id, e)}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </ListItemSecondaryAction>
    </ImpersonationUserListItem>
  );
}
